import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import './AdminLayout.scss';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const apiBaseUrl = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_URL || '';
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }, []);

  const token = localStorage.getItem('tg_admin_token');
  const storedUser = localStorage.getItem('tg_admin_user');

  const [adminUser, setAdminUser] = useState(
    storedUser ? JSON.parse(storedUser) : null
  );

  const [authLoading, setAuthLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }

    const validateAdmin = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/admin/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Session expired.');
        }

        localStorage.setItem(
          'tg_admin_user',
          JSON.stringify(data.adminUser)
        );

        setAdminUser(data.adminUser);
      } catch (error) {
        console.error('Admin session validation error:', error);

        localStorage.removeItem('tg_admin_token');
        localStorage.removeItem('tg_admin_user');

        navigate('/admin');
      } finally {
        setAuthLoading(false);
      }
    };

    validateAdmin();
  }, [apiBaseUrl, navigate, token]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('tg_admin_token');
    localStorage.removeItem('tg_admin_user');

    navigate('/admin');
  };

  const getPageTitle = () => {
    const pathname = location.pathname;

    if (pathname.includes('/admin/courses')) {
      return 'Courses';
    }

    if (pathname.includes('/admin/events')) {
      return 'Events';
    }

    if (pathname.includes('/admin/members')) {
      return 'Members';
    }

    if (pathname.includes('/admin/partners')) {
      return 'Partners';
    }

    if (pathname.includes('/admin/inquiries')) {
      return 'Inquiries';
    }

    if (pathname.includes('/admin/reports')) {
      return 'Moderation';
    }

    return 'Dashboard';
  };

  if (authLoading) {
    return (
      <div className="admin-layout-loading">
        <div className="admin-layout-loading__card">
          <span className="admin-layout-loading__mark">TG</span>

          <p>Loading admin portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <div
        className={`admin-layout__overlay ${
          sidebarOpen ? 'is-visible' : ''
        }`}
        onClick={() => setSidebarOpen(false)}
        role="presentation"
      />

      <aside
        className={`admin-sidebar ${
          sidebarOpen ? 'is-open' : ''
        }`}
      >
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__brand-mark">
            TG
          </div>

          <div className="admin-sidebar__brand-copy">
            <strong>The Tour Golfers</strong>
            <span>Admin Portal</span>
          </div>
        </div>

        <nav className="admin-sidebar__nav">
          <div className="admin-sidebar__section">
            <p className="admin-sidebar__section-title">
              Overview
            </p>

            <NavLink
              to="/admin/dashboard"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ▦
              </span>

              <span>Dashboard</span>
            </NavLink>
          </div>

          <div className="admin-sidebar__section">
            <p className="admin-sidebar__section-title">
              Manage
            </p>

            <NavLink
              to="/admin/courses"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ◉
              </span>

              <span>Courses</span>
            </NavLink>

            <NavLink
              to="/admin/events"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ◫
              </span>

              <span>Events</span>
            </NavLink>

            <NavLink
              to="/admin/members"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ◌
              </span>

              <span>Members</span>
            </NavLink>

            <NavLink
              to="/admin/partners"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ◇
              </span>

              <span>Partners</span>
            </NavLink>
          </div>

          <div className="admin-sidebar__section">
            <p className="admin-sidebar__section-title">
              Communication
            </p>

            <NavLink
              to="/admin/inquiries"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                ✉
              </span>

              <span>Inquiries</span>
            </NavLink>
          </div>

          <div className="admin-sidebar__section">
            <p className="admin-sidebar__section-title">
              Safety
            </p>

            <NavLink
              to="/admin/reports"
              className={({ isActive }) =>
                `admin-sidebar__link ${
                  isActive ? 'is-active' : ''
                }`
              }
            >
              <span className="admin-sidebar__icon">
                !
              </span>

              <span>Moderation</span>
            </NavLink>
          </div>
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__user">
            <div className="admin-sidebar__avatar">
              {adminUser?.fullName
                ? adminUser.fullName
                    .split(' ')
                    .map((name) => name[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()
                : 'A'}
            </div>

            <div className="admin-sidebar__user-copy">
              <strong>
                {adminUser?.fullName || 'Admin'}
              </strong>

              <span>
                {adminUser?.email || ''}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="admin-sidebar__logout"
            onClick={handleLogout}
          >
            Log Out
          </button>
        </div>
      </aside>

      <div className="admin-layout__main">
        <header className="admin-topbar">
          <div className="admin-topbar__left">
            <button
              type="button"
              className="admin-topbar__menu"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open admin navigation"
            >
              ☰
            </button>

            <div>
              <p className="admin-topbar__eyebrow">
                The Tour Golfers Admin
              </p>

              <h1>
                {getPageTitle()}
              </h1>
            </div>
          </div>

          <div className="admin-topbar__right">
            <span className="admin-topbar__user">
              {adminUser?.fullName ||
                adminUser?.email ||
                'Admin'}
            </span>
          </div>
        </header>

        <main className="admin-layout__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}